import { useState, useEffect, useMemo, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import AIAssistantPanel from "./components/panels/AIAssistantPanel.tsx";
import Background from "./components/ui/Background.tsx";
import ActivityTracker from "./components/time-tracking/ActivityTracker.tsx";
import FocusSessionController from "./components/time-tracking/FocusSessionController.tsx";
import Sidebar from "./sidebar/Sidebar.tsx";
import TopNavbar from "./header/Header.tsx";
import UserProfilePanel from "./components/panels/UserProfilePanel.tsx";
import SettingsPanel from "./components/panels/SettingsPanel.tsx";
import HelpPanel from "./components/panels/HelpPanel.tsx";
import DeficiencyTutorPanel from "./components/panels/DeficiencyTutorPanel.tsx";
import AITutor from "./components/panels/AITutor.tsx";
import NotificationsPanel from "./components/panels/NotificationsPanel.tsx";
import TimerPanel from "./components/panels/TimerPanel.tsx";
import { useAuth } from "./contexts/AuthContext";
import { PanelProvider, usePanels } from "./contexts/PanelContext.jsx";
import { useLocation } from "react-router-dom";
import { ApiService } from "./services/api.ts";
import { useModal } from "./hooks/useModal";
import YesToolsClassSelectionModal from "./components/modals/YesToolsClassSelectionModal.tsx";
import {
  getAllowedYesToolsClassOptions,
  getYesToolsClassSelectionError,
  isYesToolsGraduateTrackRequired,
  isYesToolsHostname,
  normalizeYesToolsClassValue,
  normalizeYesToolsGraduateTrackValue,
  YES_TOOLS_GRADUATE_TRACK_OPTIONS,
} from "./utils/yesTools.ts";

import Dashboard from "./pages/Dashboard.tsx";
import NavigatorPage from "./pages/navigator/Navigator.tsx";
import TercihLeadsPage from "./pages/tercih/TercihLeads.tsx";
import EmployeesPage from "./pages/employees/List.tsx";
import NewUserPage from "./pages/employees/NewUser.tsx";
import EditUserPage from "./pages/employees/EditUser.tsx";
import DeleteUserPage from "./pages/employees/DeleteUser.tsx";
import NewDepartmentPage from "./pages/employees/NewDepartment.tsx";
import EditDepartmentPage from "./pages/employees/EditDepartment.tsx";
import DepartmentPermissionsPage from "./pages/employees/DepartmentPermissions.tsx";
import DeleteDepartmentPage from "./pages/employees/DeleteDepartment.tsx";

import CalendarPage from "./pages/calendar/CalendarPage.tsx";
import LessonsPage from "./pages/e-education/List.tsx";
import NewLessonPage from "./pages/e-education/New.tsx";
import EditLessonPage from "./pages/e-education/Edit.tsx";
import DeleteLessonPage from "./pages/e-education/DeleteLesson.tsx";
import DeleteSectionPage from "./pages/e-education/DeleteSection.tsx";
import LoginPage from "./pages/Login.tsx";
import RegisterPage from "./pages/Register.tsx";
import SurveyPage from "./pages/surveys/List.tsx";
import SurveyResults from "./pages/surveys/Results.tsx";
import NewSurvey from "./pages/surveys/New.tsx";
import EditSurvey from "./pages/surveys/Edit.tsx";
import ParticipateSurvey from "./pages/surveys/Participate.tsx";
import MeetingsPage from "./pages/meetings/List.tsx";
import NewMeetingPage from "./pages/meetings/New.tsx";
import EditMeetingPage from "./pages/meetings/Edit.tsx";
import CourseDetailPage from "./pages/e-education/Detail.tsx";
import { VideoPlayerPage } from "./pages/e-education/Play.tsx";
import PlanningPage from "./pages/planning/Planning.tsx";
import AppointmentsPage from "./pages/appointments/Appointments.tsx";
import TodosPage from "./pages/todos/List.tsx";
import TasksPage from "./pages/tasks/List.tsx";
import AnnouncementsPage from "./pages/announcements/List.tsx";
import NewAnnouncementPage from "./pages/announcements/New.tsx";
import EditAnnouncementPage from "./pages/announcements/Edit.tsx";
import FilesPage from "./pages/files/List.tsx";
import PracticeExamsPage from "./pages/practice-exams/List.tsx";
import ReportCards from "./pages/practice-exams/ReportCards.tsx";
import MyReportCards from "./pages/practice-exams/MyReportCards.tsx";
import WhiteboardPage from "./pages/whiteboard/Whiteboard.tsx";
import SystemSettingsPage from "./pages/system-settings/SystemSettings.tsx";
import SpeedReadingPage from "./pages/speed-reading/SpeedReading.tsx";
import VerifyPhonePage from "./pages/VerifyPhone.tsx";
import TercihRobotuPage from "./pages/TercihRobotu.tsx";
import QuestionBankListPage from "./pages/question-bank/List.tsx";
import InDesignQuestionBank from "./pages/question-bank/InDesignQuestionBank.tsx";
import MaarifTestGeneratorPage from "./pages/maarif-test-generator/List.tsx";
import MaarifTestGeneratorWorkspacePage from "./pages/maarif-test-generator/Generator.tsx";
import MaarifQuestionWizardListPage from "./pages/maarif-question-wizard/List.tsx";
import MaarifQuestionWizardWorkspacePage from "./pages/maarif-question-wizard/Workspace.tsx";
import MaarifCurriculumTestPage from "./pages/maarif-question-wizard/CurriculumTest.tsx";
import MaarifBatchGeneratePage from "./pages/maarif-batch/BatchGenerate.tsx";
import CafeMenuPage from "./pages/cafe-menu/CafeMenu.tsx";
import CafeCampaignsPage from "./pages/cafe-campaigns/CafeCampaigns.tsx";
import AkademiQrPage from "./pages/akademi-qr/AkademiQr.tsx";
import TimeCenterPage from "./pages/time-center/TimeCenter.tsx";
import SocialModuleLayout from "./pages/social/SocialModuleLayout.jsx";
import PaymentPage from "./pages/payment/PaymentPage.tsx";
import PaymentResultPage from "./pages/payment/PaymentResultPage.tsx";
import IlmekContentPackListPage from "./pages/ilmek/List.tsx";
import IlmekContentPackNewPage from "./pages/ilmek/New.tsx";
import IlmekContentPackEditPage from "./pages/ilmek/Edit.tsx";
import IlmekOgrenPage from "./pages/ilmek/Ogren.tsx";
import IlmekSessionPage from "./pages/ilmek/Session.tsx";

const paidModulePaths = [
  ["announcements", "/announcements"],
  ["surveys", "/surveys"],
  ["employees", "/employees"],
  ["meetings", "/meetings"],
  ["calendar", "/calendar"],
  ["lessons", "/lessons"],
  ["planning", "/planning"],
  ["appointments", "/appointments"],
  ["todos", "/todos"],
  ["tasks", "/tasks"],
  ["files", "/files"],
  ["practice-exams", "/practice-exams"],
  ["maarif-test-generator", "/maarif-test-generator"],
  ["maarif-question-wizard", "/maarif-question-wizard"],
  ["question-bank-indesign", "/question-bank-indesign"],
  ["social", "/social"],
  ["whiteboard", "/whiteboard"],
  ["speed-reading", "/speed-reading"],
  ["cafe-menu", "/cafe-menu"],
  ["cafe-campaigns", "/cafe-campaigns"],
  ["akademi-qr", "/akademi-qr"],
  ["time-center", "/time-center"],
];

function PaidModuleRouteGuard({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const isYesToolsDomain = typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  if (!isYesToolsDomain) return children;
  const match = paidModulePaths.find(([, path]) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  if (!match) return children;
  const access = user?.paymentAccess?.modules?.find((item) => item.id === match[0])?.access;
  if (access === "PAYMENT_REQUIRED") {
    const destination = `/payment?module=${encodeURIComponent(match[0])}&returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
    return <Navigate to={destination} replace />;
  }
  if (access === "HIDDEN") return <Navigate to="/dashboard" replace />;
  return children;
}

function YesToolsPaymentRoute({ children }) {
  const isYesToolsDomain = typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  return isYesToolsDomain ? children : <Navigate to="/dashboard" replace />;
}

function ProtectedLayout() {
  const location = useLocation();
  const { user, refreshUserData } = useAuth();
  const { showToast } = useModal();
  const [sidebarExpanded, setSidebarExpanded] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [aiPanelOpen, setAIPanelOpen] = useState(false);
  const [aiPanelExpanded, setAIPanelExpanded] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showDeficiencyTutorPanel, setShowDeficiencyTutorPanel] = useState(false);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [deficiencyTutorData, setDeficiencyTutorData] = useState({ gainId: "", allGains: [] });
  const [showAITutorPanel, setShowAITutorPanel] = useState(false);
  const [aiTutorContext, setAITutorContext] = useState({});
  const [showTimerPanel, setShowTimerPanel] = useState(false);
  const [showTopicTutorPanel, setShowTopicTutorPanel] = useState(false);
  const [topicTutorData, setTopicTutorData] = useState({ contentTitle: "", gains: [], gainIds: [], videos: [], lesson: null, topic: null });
  const [additionalApps, setAdditionalApps] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSidebarSort, setCurrentSidebarSort] = useState([]);
  const [selectedClassLevel, setSelectedClassLevel] = useState("");
  const [selectedGraduateTrack, setSelectedGraduateTrack] = useState("");
  const [savingClassSelection, setSavingClassSelection] = useState(false);
  const previousRouteRef = useRef(`${location.pathname}${location.search}${location.hash}`);
  const { rightPanelCount, maxRightPanelWidth, setMainContentRightInsetActive } = usePanels();

  const mainContentUsesRightInset = useMemo(
    () =>
      !isMobile &&
      (aiPanelOpen ||
        rightPanelCount > 0 ||
        showUserProfile ||
        showSettingsPanel ||
        showHelpPanel ||
        showDeficiencyTutorPanel ||
        showTopicTutorPanel ||
        showNotificationsPanel ||
        showTimerPanel ||
        showAITutorPanel),
    [
      isMobile,
      aiPanelOpen,
      rightPanelCount,
      showUserProfile,
      showSettingsPanel,
      showHelpPanel,
      showDeficiencyTutorPanel,
      showTopicTutorPanel,
      showNotificationsPanel,
      showTimerPanel,
      showAITutorPanel,
    ]
  );

  useEffect(() => {
    setMainContentRightInsetActive(mainContentUsesRightInset);
  }, [mainContentUsesRightInset, setMainContentRightInsetActive]);

  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  const shouldShowYesToolsClassModal = Boolean(
    isYesToolsDomain && user?.needsClassSelection && !user?.isSuperAdmin
  );
  const allowedYesToolsClassOptions = getAllowedYesToolsClassOptions(user?.birthDate);
  const shouldShowGraduateTrackSelect = isYesToolsGraduateTrackRequired(selectedClassLevel);
  const yesToolsClassSelectionError = getYesToolsClassSelectionError(
    user?.birthDate,
    selectedClassLevel,
    selectedGraduateTrack
  );

  useEffect(() => {
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (previousRouteRef.current === currentRoute) return;

    previousRouteRef.current = currentRoute;
    if (showAITutorPanel) {
      setShowAITutorPanel(false);
      setAITutorContext({});
    }
  }, [location.pathname, location.search, location.hash, showAITutorPanel]);

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setSidebarExpanded(width >= 768);
      setIsMobile(width < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (shouldShowYesToolsClassModal) {
      setSelectedClassLevel(normalizeYesToolsClassValue(user?.selectedClassLevel) || "");
      setSelectedGraduateTrack(
        normalizeYesToolsGraduateTrackValue(
          user?.selectedGraduateTrack ||
            user?.metadata?.yesToolsGraduateTrack ||
            user?.metadata?.graduateTrack ||
            user?.selectedGraduateTrackLabel ||
            user?.metadata?.yesToolsGraduateTrackLabel ||
            user?.metadata?.graduateTrackLabel
        ) || ""
      );
    } else {
      setSelectedClassLevel("");
      setSelectedGraduateTrack("");
    }
  }, [
    shouldShowYesToolsClassModal,
    user?.selectedClassLevel,
    user?.selectedGraduateTrack,
    user?.selectedGraduateTrackLabel,
    user?.metadata?.graduateTrack,
    user?.metadata?.graduateTrackLabel,
    user?.metadata?.yesToolsGraduateTrack,
    user?.metadata?.yesToolsGraduateTrackLabel,
  ]);

  useEffect(() => {
    const handleToggleUserProfile = () => {
      setShowUserProfile((prev) => {
        const willOpen = !prev;
        if (willOpen) {
          setAIPanelOpen(false);
          setAIPanelExpanded(false);
          setShowSettingsPanel(false);
          setShowHelpPanel(false);
          setShowDeficiencyTutorPanel(false);
          setShowNotificationsPanel(false);
          setShowTimerPanel(false);
        }
        return willOpen;
      });
    };

    window.addEventListener("toggleUserProfile", handleToggleUserProfile);

    return () => {
      window.removeEventListener("toggleUserProfile", handleToggleUserProfile);
    };
  }, []);

  useEffect(() => {
    const handleToggleSettingsPanel = () => {
      setShowSettingsPanel((prev) => {
        const willOpen = !prev;
        if (willOpen) {
          setAIPanelOpen(false);
          setAIPanelExpanded(false);
          setShowUserProfile(false);
          setShowHelpPanel(false);
          setShowDeficiencyTutorPanel(false);
          setShowNotificationsPanel(false);
          setShowTimerPanel(false);
        }
        return willOpen;
      });
    };

    window.addEventListener("toggleSettingsPanel", handleToggleSettingsPanel);

    return () => {
      window.removeEventListener("toggleSettingsPanel", handleToggleSettingsPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleHelpPanel = () => {
      setShowHelpPanel((prev) => {
        const willOpen = !prev;
        if (willOpen) {
          setAIPanelOpen(false);
          setAIPanelExpanded(false);
          setShowUserProfile(false);
          setShowSettingsPanel(false);
          setShowDeficiencyTutorPanel(false);
          setShowNotificationsPanel(false);
          setShowTimerPanel(false);
        }
        return willOpen;
      });
    };

    window.addEventListener("toggleHelpPanel", handleToggleHelpPanel);

    return () => {
      window.removeEventListener("toggleHelpPanel", handleToggleHelpPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleDeficiencyTutorPanel = (event) => {
      const { gainId, allGains, onGainResolved } = event.detail || {};
      if (gainId) {
        setDeficiencyTutorData({ gainId, allGains: allGains || [], onGainResolved });
        setShowDeficiencyTutorPanel(true);
        setAIPanelOpen(false);
        setAIPanelExpanded(false);
        setShowUserProfile(false);
        setShowSettingsPanel(false);
        setShowHelpPanel(false);
        setShowNotificationsPanel(false);
      } else {
        setShowDeficiencyTutorPanel(false);
        setDeficiencyTutorData({ gainId: "", allGains: [] });
      }
    };

    window.addEventListener("toggleDeficiencyTutorPanel", handleToggleDeficiencyTutorPanel);

    return () => {
      window.removeEventListener("toggleDeficiencyTutorPanel", handleToggleDeficiencyTutorPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleAITutorPanel = (event) => {
      const context = event.detail?.context || event.detail || {};
      if (context) {
        setAITutorContext(context);
        setShowAITutorPanel(true);
        setAIPanelOpen(false);
        setAIPanelExpanded(false);
        setShowUserProfile(false);
        setShowSettingsPanel(false);
        setShowHelpPanel(false);
        setShowNotificationsPanel(false);
        setShowTimerPanel(false);
      }
    };

    window.addEventListener("toggleAITutorPanel", handleToggleAITutorPanel);

    return () => {
      window.removeEventListener("toggleAITutorPanel", handleToggleAITutorPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleTopicTutorPanel = (event) => {
      const { contentTitle, gains, gainIds, videos, lesson, topic, onComplete } = event.detail || {};
      if (contentTitle) {
        setTopicTutorData({ contentTitle, gains: gains || [], gainIds: gainIds || [], videos: videos || [], lesson, topic, onComplete });
        setShowTopicTutorPanel(true);
        setAIPanelOpen(false);
        setAIPanelExpanded(false);
        setShowUserProfile(false);
        setShowSettingsPanel(false);
        setShowHelpPanel(false);
        setShowDeficiencyTutorPanel(false);
        setShowNotificationsPanel(false);
      } else {
        setShowTopicTutorPanel(false);
        setTopicTutorData({ contentTitle: "", gains: [], gainIds: [], videos: [], lesson: null, topic: null });
      }
    };

    window.addEventListener("toggleTopicTutorPanel", handleToggleTopicTutorPanel);

    return () => {
      window.removeEventListener("toggleTopicTutorPanel", handleToggleTopicTutorPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleNotificationsPanel = () => {
      setShowNotificationsPanel((prev) => {
        const willOpen = !prev;
        if (willOpen) {
          setAIPanelOpen(false);
          setAIPanelExpanded(false);
          setShowUserProfile(false);
          setShowSettingsPanel(false);
          setShowHelpPanel(false);
          setShowDeficiencyTutorPanel(false);
          setShowTimerPanel(false);
        }
        return willOpen;
      });
    };

    window.addEventListener("toggleNotificationsPanel", handleToggleNotificationsPanel);

    return () => {
      window.removeEventListener("toggleNotificationsPanel", handleToggleNotificationsPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleTimerPanel = () => {
      setShowTimerPanel((prev) => {
        const willOpen = !prev;
        if (willOpen) {
          setAIPanelOpen(false);
          setAIPanelExpanded(false);
          setShowUserProfile(false);
          setShowSettingsPanel(false);
          setShowHelpPanel(false);
          setShowDeficiencyTutorPanel(false);
          setShowNotificationsPanel(false);
        }
        return willOpen;
      });
    };

    window.addEventListener("toggleTimerPanel", handleToggleTimerPanel);

    return () => {
      window.removeEventListener("toggleTimerPanel", handleToggleTimerPanel);
    };
  }, []);

  useEffect(() => {
    const handleToggleAIPanel = () => {
      toggleAIPanel();
    };

    window.addEventListener("toggleAIPanel", handleToggleAIPanel);

    return () => {
      window.removeEventListener("toggleAIPanel", handleToggleAIPanel);
    };
  }, [aiPanelOpen]);

  const toggleAIPanel = () => {
    const willOpen = !aiPanelOpen;
    setAIPanelOpen(willOpen);
    if (willOpen) {
      setShowUserProfile(false);
      setShowSettingsPanel(false);
      setShowHelpPanel(false);
      setShowDeficiencyTutorPanel(false);
      setShowNotificationsPanel(false);
      setShowTimerPanel(false);
      window.dispatchEvent(new CustomEvent("closeRightPanels"));
    } else {
      setAIPanelExpanded(false);
    }
  };

  const toggleAIPanelExpand = () => {
    setAIPanelExpanded(!aiPanelExpanded);
  };

  const closeUserProfile = () => {
    setShowUserProfile(false);
  };

  const closeSettingsPanel = () => {
    setShowSettingsPanel(false);
  };

  const closeHelpPanel = () => {
    setShowHelpPanel(false);
  };

  const closeNotificationsPanel = () => {
    setShowNotificationsPanel(false);
  };

  const handleRemoveApp = (appId) => {
    setAdditionalApps((prev) => prev.filter((app) => app.id !== appId));
  };

  const toggleEditMode = async () => {
    if (isEditMode) {
      try {
        if (currentSidebarSort && currentSidebarSort.length > 0) {
          await ApiService.updateMySidebarSort(currentSidebarSort);
          localStorage.setItem("sidebarSort", JSON.stringify(currentSidebarSort));
          window.dispatchEvent(new CustomEvent("sidebarSortUpdated"));
        }
        setIsEditMode(false);
      } catch (error) {
        console.error("Sıralama kaydedilemedi:", error);
        alert("Sıralama kaydedilirken bir hata oluştu.");
      }
    } else {
      setIsEditMode(true);
    }
  };

  const cancelEditMode = () => {
    setCurrentSidebarSort([]);
    setIsEditMode(false);
    window.dispatchEvent(new CustomEvent("sidebarSortUpdated"));
  };

  const handleSidebarSortChange = (newSort) => {
    setCurrentSidebarSort(newSort);
  };

  const handleYesToolsClassSelectionSave = async () => {
    if (!selectedClassLevel) {
      showToast("Lütfen sınıfınızı seçiniz.", "warning");
      return;
    }

    if (yesToolsClassSelectionError) {
      showToast(yesToolsClassSelectionError, "warning");
      return;
    }

    try {
      setSavingClassSelection(true);
      const response = await ApiService.updateMyClassSelection({
        selectedClassLevel,
        selectedGraduateTrack: shouldShowGraduateTrackSelect ? selectedGraduateTrack : undefined,
      });
      await refreshUserData();
      showToast(response?.message || "Sınıfınız başarıyla kaydedildi.", "success");
    } catch (error) {
      showToast(error?.message || "Sınıf kaydedilirken bir hata oluştu.", "error");
    } finally {
      setSavingClassSelection(false);
    }
  };

  return (
    <div className="font-sans">
      <div className="no-print">
        <Background />

        <TopNavbar
          toggleSidebar={toggleSidebar}
          toggleAIPanel={toggleAIPanel}
          isEditMode={isEditMode}
          toggleEditMode={toggleEditMode}
          cancelEditMode={cancelEditMode}
        />
      </div>

      {typeof window !== "undefined" && (
        <div className="no-print">
          <ActivityTracker />
          <FocusSessionController />
          <Sidebar
            isExpanded={sidebarExpanded}
            toggleSidebar={toggleSidebar}
            additionalApps={additionalApps}
            onRemoveApp={handleRemoveApp}
            isEditMode={isEditMode}
            onSortChange={handleSidebarSortChange}
          />
        </div>
      )}

      <main
        className={`
          min-h-[calc(100dvh-60px)] 
          max-h-[calc(100dvh-60px)]
          mt-[60px]
          transition-all 
          duration-500
          ease-in-out
          relative
          overflow-y-auto
          ml-0 mr-0
          ${sidebarExpanded ? "md:ml-64" : "md:ml-[50px]"}
          ${aiPanelOpen || showUserProfile || showSettingsPanel || showHelpPanel || showDeficiencyTutorPanel || showTopicTutorPanel || showNotificationsPanel || showAITutorPanel || showTimerPanel ? "md:mr-80" : ""}
          ${isDashboard ? "scrollbar-hide" : "custom-scrollbar"}
          ${aiPanelExpanded ? "opacity-0 pointer-events-none scale-95" : "opacity-100 scale-100"}
        `}
        style={{
          marginRight:
            !isMobile && (aiPanelOpen || rightPanelCount > 0 || showUserProfile || showSettingsPanel || showHelpPanel || showDeficiencyTutorPanel || showTopicTutorPanel || showNotificationsPanel || showAITutorPanel || showTimerPanel)
              ? `${Math.max(320, Number(maxRightPanelWidth || 0))}px`
              : 0,
        }}>
        <PaidModuleRouteGuard>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/navigator" element={<NavigatorPage />} />
          <Route path="/tercih-leadleri" element={<TercihLeadsPage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/announcements/new" element={<NewAnnouncementPage />} />
          <Route path="/announcements/edit/:id" element={<EditAnnouncementPage />} />
          <Route path="/surveys" element={<SurveyPage />} />
          <Route path="/surveys/new" element={<NewSurvey />} />
          <Route path="/surveys/edit/:id" element={<EditSurvey />} />
          <Route path="/surveys/participate/:id" element={<ParticipateSurvey />} />
          <Route path="/surveys/results/:id" element={<SurveyResults />} />
          <Route path="/ilmek" element={<IlmekContentPackListPage />} />
          <Route path="/ilmek/new" element={<IlmekContentPackNewPage />} />
          <Route path="/ilmek/edit/:id" element={<IlmekContentPackEditPage />} />
          <Route path="/ilmek/ogren" element={<IlmekOgrenPage />} />
          <Route path="/ilmek/ogren/:journeyId" element={<IlmekSessionPage />} />
          <Route path="/meetings" element={<MeetingsPage />} />
          <Route path="/meetings/new" element={<NewMeetingPage />} />
          <Route path="/meetings/edit/:id" element={<EditMeetingPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/employees/users/new" element={<NewUserPage />} />
          <Route path="/employees/users/edit/:userId" element={<EditUserPage />} />
          <Route path="/employees/users/:userId/delete" element={<DeleteUserPage />} />
          <Route path="/employees/departments/new" element={<NewDepartmentPage />} />
          <Route path="/employees/departments/edit/:departmentId" element={<EditDepartmentPage />} />
          <Route path="/employees/departments/:departmentId/permissions" element={<DepartmentPermissionsPage />} />
          <Route path="/employees/departments/:departmentId/delete" element={<DeleteDepartmentPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/lessons" element={<LessonsPage />} />
          <Route path="/lessons/new" element={<NewLessonPage />} />
          <Route path="/lessons/edit/:lessonId" element={<EditLessonPage />} />
          <Route path="/lessons/:lessonId/delete" element={<DeleteLessonPage />} />
          <Route path="/lessons/:lessonId/sections/:sectionId/delete" element={<DeleteSectionPage />} />
          <Route path="/lessons/detail/:lessonId" element={<CourseDetailPage />} />
          <Route path="/lessons/play/:lessonId" element={<VideoPlayerPage />} />
          <Route path="/planning" element={<PlanningPage />} />
          <Route path="/appointments" element={<AppointmentsPage />} />
          <Route path="/todos" element={<TodosPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/practice-exams" element={<PracticeExamsPage />} />
          <Route path="/maarif-test-generator" element={<MaarifTestGeneratorPage />} />
          <Route path="/maarif-test-generator/new" element={<MaarifTestGeneratorWorkspacePage />} />
          <Route path="/maarif-question-wizard" element={<MaarifQuestionWizardListPage />} />
          <Route path="/maarif-question-wizard/curriculum-test" element={<MaarifCurriculumTestPage />} />
          <Route path="/maarif-batch" element={<MaarifBatchGeneratePage />} />
          <Route path="/maarif-question-wizard/new" element={<MaarifQuestionWizardWorkspacePage />} />
          <Route path="/maarif-question-wizard/:id" element={<MaarifQuestionWizardWorkspacePage />} />
          <Route path="/question-bank-indesign" element={<QuestionBankListPage />} />
          <Route path="/question-bank-indesign/:id/edit" element={<InDesignQuestionBank />} />
          <Route path="/report-cards" element={<ReportCards />} />
          <Route path="/my-report-cards" element={<MyReportCards />} />
          <Route path="/whiteboard" element={<WhiteboardPage />} />
          <Route path="/speed-reading" element={<SpeedReadingPage />} />
          <Route path="/cafe-menu" element={<CafeMenuPage />} />
          <Route path="/cafe-campaigns" element={<CafeCampaignsPage />} />
          <Route path="/akademi-qr" element={<AkademiQrPage />} />
          <Route path="/time-center" element={<TimeCenterPage />} />
          <Route path="/social/*" element={<SocialModuleLayout />} />
          <Route path="/payment" element={<YesToolsPaymentRoute><PaymentPage /></YesToolsPaymentRoute>} />
          <Route path="/payment/success" element={<YesToolsPaymentRoute><PaymentResultPage success /></YesToolsPaymentRoute>} />
          <Route path="/payment/failure" element={<YesToolsPaymentRoute><PaymentResultPage success={false} /></YesToolsPaymentRoute>} />

          <Route path="/settings" element={<SystemSettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PaidModuleRouteGuard>
      </main>

      <div className="no-print">
        <AIAssistantPanel
          isOpen={aiPanelOpen}
          onClose={toggleAIPanel}
          isHomePage={isDashboard}
          isExpanded={aiPanelExpanded}
          onToggleExpand={toggleAIPanelExpand}
          sidebarExpanded={sidebarExpanded}
        />
        <UserProfilePanel isOpen={showUserProfile} onClose={closeUserProfile} isHomePage={isDashboard} />
        <TimerPanel isOpen={showTimerPanel} onClose={() => setShowTimerPanel(false)} isHomePage={isDashboard} />
        <SettingsPanel isOpen={showSettingsPanel} onClose={closeSettingsPanel} isHomePage={isDashboard} />
        <HelpPanel isOpen={showHelpPanel} onClose={closeHelpPanel} isHomePage={isDashboard} />
        <AITutor
          isOpen={showAITutorPanel}
          context={aiTutorContext}
          onClose={() => setShowAITutorPanel(false)}
          sidebarExpanded={sidebarExpanded}
        />
        <DeficiencyTutorPanel
          isOpen={showDeficiencyTutorPanel}
          onClose={() => {
            setShowDeficiencyTutorPanel(false);
            setDeficiencyTutorData({ gainId: "", allGains: [] });
          }}
          gainId={deficiencyTutorData.gainId}
          allGains={deficiencyTutorData.allGains}
          onGainResolved={deficiencyTutorData.onGainResolved}
          sidebarExpanded={sidebarExpanded}
        />

        <DeficiencyTutorPanel
          isOpen={showTopicTutorPanel}
          onClose={() => {
            setShowTopicTutorPanel(false);
            setTopicTutorData({ contentTitle: "", gains: [], gainIds: [], videos: [], lesson: null, topic: null });
            if (topicTutorData.onComplete) {
              topicTutorData.onComplete();
            }
          }}
          gainId=""
          mode="topic"
          topicData={topicTutorData}
          onGainResolved={topicTutorData.onComplete}
          sidebarExpanded={sidebarExpanded}
        />
        <NotificationsPanel isOpen={showNotificationsPanel} onClose={closeNotificationsPanel} isHomePage={isDashboard} />
        <YesToolsClassSelectionModal
          isOpen={shouldShowYesToolsClassModal}
          selectedClassLevel={selectedClassLevel}
          selectedGraduateTrack={selectedGraduateTrack}
          options={allowedYesToolsClassOptions}
          graduateTrackOptions={YES_TOOLS_GRADUATE_TRACK_OPTIONS}
          showGraduateTrackSelect={shouldShowGraduateTrackSelect}
          errorText={yesToolsClassSelectionError || undefined}
          saving={savingClassSelection}
          onChange={(nextClassLevel) => {
            setSelectedClassLevel(nextClassLevel);
            if (!isYesToolsGraduateTrackRequired(nextClassLevel)) {
              setSelectedGraduateTrack("");
            }
          }}
          onGraduateTrackChange={setSelectedGraduateTrack}
          onConfirm={handleYesToolsClassSelectionSave}
        />
      </div>
    </div>
  );
}

function Layouts() {
  const { isAuthenticated, loading, systemConfig } = useAuth();

  useEffect(() => {
    if (systemConfig?.companyName) {
      document.title = systemConfig.companyName;
    } else {
      document.title = "Yeni Eğitim Sistemi";
    }
  }, [systemConfig?.companyName]);

  useEffect(() => {
    if (systemConfig) {
      if (systemConfig.favicon96x96) {
        const link = document.querySelector("link[sizes='96x96']");
        if (link) link.href = systemConfig.favicon96x96;
      }
      if (systemConfig.faviconSvg) {
        const link = document.querySelector("link[type='image/svg+xml']");
        if (link) link.href = systemConfig.faviconSvg;
      }
      if (systemConfig.faviconIco) {
        const link = document.querySelector("link[rel='shortcut icon']");
        if (link) link.href = systemConfig.faviconIco;
      }
      if (systemConfig.faviconAppleTouchIcon) {
        const link = document.querySelector("link[rel='apple-touch-icon']");
        if (link) link.href = systemConfig.faviconAppleTouchIcon;
      }
      if (systemConfig.siteWebmanifest) {
        const link = document.querySelector("link[rel='manifest']");
        if (link) link.href = systemConfig.siteWebmanifest;
      }
    }
  }, [
    systemConfig?.favicon96x96,
    systemConfig?.faviconSvg,
    systemConfig?.faviconIco,
    systemConfig?.faviconAppleTouchIcon,
    systemConfig?.siteWebmanifest,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <PanelProvider>
        <Routes>
          <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/register" element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />} />
          <Route path="/verify-phone" element={<VerifyPhonePage />} />
          <Route path="/tercih-robotu" element={<TercihRobotuPage />} />
          <Route path="/*" element={isAuthenticated ? <ProtectedLayout /> : <Navigate to="/login" replace />} />
        </Routes>
      </PanelProvider>
    </Router>
  );
}

export default Layouts;
